import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, History } from "lucide-react";
import { getChangelog } from "@/lib/api";

/**
 * Very small markdown → JSX renderer covering only the patterns used in
 * /app/memory/CHANGELOG.md:
 *   - `# H1` / `## H2` / `### H3`
 *   - `---` horizontal rule
 *   - `- ` bullet list items (flat; inline formatting only)
 *   - `**bold**` and `` `code` `` inline
 *   - Blank line separates blocks
 */
function renderMarkdown(md) {
  if (!md) return [];
  const lines = md.split(/\r?\n/);

  const blocks = [];
  let currentList = null;
  const flushList = () => {
    if (currentList) {
      blocks.push({ type: "ul", items: currentList });
      currentList = null;
    }
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/g, "");

    if (line === "") { flushList(); continue; }
    if (/^---+$/.test(line)) { flushList(); blocks.push({ type: "hr" }); continue; }

    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flushList();
      blocks.push({ type: `h${h[1].length}`, text: h[2].trim() });
      continue;
    }

    if (/^-\s+/.test(line)) {
      const text = line.replace(/^-\s+/, "");
      currentList = currentList || [];
      currentList.push(text);
      continue;
    }

    flushList();
    blocks.push({ type: "p", text: line });
  }
  flushList();
  return blocks;
}

// Inline formatting: **bold**, `code`, and http(s) links → <a>.
function inline(text) {
  if (typeof text !== "string") return text;
  // Tokenise on the three patterns.
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`|https?:\/\/[^\s)]+)/g;
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const tok = match[0];
    if (tok.startsWith("**")) {
      parts.push(
        <strong key={`b-${i++}`} className="text-foreground">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else if (tok.startsWith("`")) {
      parts.push(
        <code key={`c-${i++}`} className="px-1.5 py-0.5 rounded bg-card/70 border border-border text-primary/90 text-[0.85em]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else {
      parts.push(
        <a key={`l-${i++}`} href={tok} target="_blank" rel="noreferrer" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors">
          {tok}
        </a>,
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function Block({ block, idx }) {
  switch (block.type) {
    case "h1":
      return (
        <h1 key={idx} className="serif text-4xl md:text-5xl font-light text-foreground mt-2 mb-6 leading-tight" data-testid={`changelog-h1-${idx}`}>
          {inline(block.text)}
        </h1>
      );
    case "h2":
      return (
        <h2 key={idx} className="serif text-2xl md:text-3xl font-light text-foreground mt-12 mb-4 pb-2 border-b border-border" data-testid={`changelog-h2-${idx}`}>
          {inline(block.text)}
        </h2>
      );
    case "h3":
      return (
        <h3 key={idx} className="overline mt-8 mb-3" data-testid={`changelog-h3-${idx}`}>
          {inline(block.text)}
        </h3>
      );
    case "hr":
      return <hr key={idx} className="my-14 border-border/60" />;
    case "ul":
      return (
        <ul key={idx} className="mt-1 mb-4 space-y-2 pl-1" data-testid={`changelog-ul-${idx}`}>
          {block.items.map((t, i) => (
            <li key={i} className="flex gap-3 text-sm text-foreground/85 leading-relaxed">
              <span className="text-primary/70 mt-1 text-xs shrink-0">✦</span>
              <span>{inline(t)}</span>
            </li>
          ))}
        </ul>
      );
    case "p":
    default:
      return (
        <p key={idx} className="my-3 text-sm text-foreground/85 leading-relaxed">
          {inline(block.text)}
        </p>
      );
  }
}

export default function AdminChangelog() {
  const [md, setMd] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const res = await getChangelog();
      setMd(res.content || "");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed to load changelog";
      setErr(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const blocks = useMemo(() => renderMarkdown(md), [md]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-changelog">
      <main className="mx-auto max-w-[900px] px-6 lg:px-10 py-14">
        <div className="flex items-start justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 overline text-primary/80">
              <History size={12} strokeWidth={1.5} /> Change log
            </div>
            <h1 className="serif text-4xl md:text-5xl font-light text-foreground mt-3 leading-tight">
              What&rsquo;s new
            </h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl leading-relaxed">
              A running history of features, changes and fixes made to the site. Sourced from{" "}
              <code className="text-primary/90 text-[0.85em]">/app/memory/CHANGELOG.md</code>.
            </p>
          </div>
          <button
            type="button"
            data-testid="changelog-refresh"
            onClick={load}
            disabled={loading}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 border border-border text-[0.68rem] uppercase tracking-[0.2em] text-foreground/85 hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
          >
            <RefreshCcw size={12} strokeWidth={1.5} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading" : "Refresh"}
          </button>
        </div>

        {err ? (
          <div className="border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive-foreground">
            {err}
          </div>
        ) : loading && !md ? (
          <div className="text-sm text-muted-foreground italic">Loading change log…</div>
        ) : (
          <article className="prose-nelum" data-testid="changelog-content">
            {blocks.map((b, i) => (
              <Block key={i} idx={i} block={b} />
            ))}
          </article>
        )}
      </main>
    </div>
  );
}
